"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import Link from "next/link";

type Clip = {
  id: string;
  aweme_id: string;
  author: string;
  path: string;
  title_zh: string;
  summary: string;
  score: number;
  level?: string;
  content_type?: string;
  topics: string[];
  key_concepts: string[];
  start_s: number;
  end_s: number;
  duration_s: number;
  reason: string;
  has_video: boolean;
  has_fine_asr: boolean;
  video_url?: string | null;
  detail_url?: string;
};

type ClipsResponse = {
  updated_at?: string;
  stats?: {
    sources?: number;
    clips?: number;
    by_author?: Record<string, number>;
    by_topic?: Record<string, number>;
  };
  total_matched?: number;
  clips?: Clip[];
  error?: string;
};

const TOPIC_LABEL: Record<string, string> = {
  preflop: "翻前",
  postflop: "翻后",
  gto: "GTO",
  basics: "基础",
  exploit: "剥削",
  review: "复盘",
  psychology: "心理",
  drill: "测验",
  cbet: "持续下注",
  "c-bet": "持续下注",
};

function normalizeTopicKey(t: string): string {
  const k = t.toLowerCase();
  if (k === "c-bet" || k === "cbet") return "cbet";
  return t;
}

export default function ClipsPage() {
  const [q, setQ] = useState("");
  const [topic, setTopic] = useState("");
  const [author, setAuthor] = useState("");
  const [data, setData] = useState<ClipsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState<Clip | null>(null);

  const query = useMemo(() => {
    const p = new URLSearchParams();
    if (q.trim()) p.set("q", q.trim());
    if (topic) p.set("topic", topic);
    if (author) p.set("author", author);
    p.set("limit", "80");
    p.set("minScore", "50");
    return p.toString();
  }, [q, topic, author]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/clips?${query}`, { cache: "no-store" });
      setData(await res.json());
    } catch (e) {
      setData({ error: String(e), clips: [] });
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    queueMicrotask(() => void refresh());
  }, [refresh]);

  const topics = (() => {
    const merged: Record<string, number> = {};
    for (const [t, n] of Object.entries(data?.stats?.by_topic || {})) {
      const k = normalizeTopicKey(t);
      merged[k] = (merged[k] || 0) + n;
    }
    return Object.entries(merged).sort((a, b) => b[1] - a[1]);
  })();
  const authors = Object.entries(data?.stats?.by_author || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 12);
  const clips = data?.clips || [];

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">切片包</h1>
          <p className="mt-1 text-sm text-zinc-500">
            从 extract.clip_suggestions 导出 · 供 030b / agent 消费 · 索引{" "}
            {data?.stats?.clips ?? "—"} 条 · 匹配 {data?.total_matched ?? "—"}
            {data?.updated_at ? ` · 更新 ${data.updated_at}` : ""}
          </p>
        </div>
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void refresh();
          }}
        >
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="搜：CBet / 3bet / 剥削…"
            className="w-56 rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-sm outline-none focus:border-emerald-500/50"
          />
          <button
            type="submit"
            className="rounded-lg bg-emerald-600/90 px-3 py-1.5 text-sm text-white"
          >
            搜
          </button>
        </form>
      </header>

      {data?.error && (
        <p className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-200">
          {data.error}
          <span className="mt-1 block text-xs text-rose-300/80">
            运行：`.venv/bin/python scripts/export_clips.py`
          </span>
        </p>
      )}

      <section className="space-y-2">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setTopic("")}
            className={`rounded-full px-3 py-1 text-xs ${
              !topic
                ? "bg-emerald-500/20 text-emerald-300"
                : "bg-zinc-900 text-zinc-400"
            }`}
          >
            全部话题
          </button>
          {topics.map(([t, n]) => (
            <button
              key={t}
              type="button"
              onClick={() => setTopic(topic === t ? "" : t)}
              className={`rounded-full px-3 py-1 text-xs ${
                topic === t
                  ? "bg-emerald-500/20 text-emerald-300"
                  : "bg-zinc-900 text-zinc-400 hover:text-zinc-200"
              }`}
            >
              {TOPIC_LABEL[t] || t} {n}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          {authors.map(([a, n]) => (
            <button
              key={a}
              type="button"
              onClick={() => setAuthor(author === a ? "" : a)}
              className={`rounded-full px-3 py-1 text-xs ${
                author === a
                  ? "bg-violet-500/20 text-violet-200"
                  : "bg-zinc-900 text-zinc-500 hover:text-zinc-300"
              }`}
            >
              {a} {n}
            </button>
          ))}
        </div>
      </section>

      <div className="grid gap-4 lg:grid-cols-[1fr_360px]">
        <div className="space-y-2">
          {loading && (
            <p className="text-sm text-zinc-500">加载切片索引…</p>
          )}
          {!loading && clips.length === 0 && (
            <p className="rounded-xl border border-zinc-800 bg-zinc-900/40 px-4 py-8 text-center text-sm text-zinc-500">
              无匹配切片。
            </p>
          )}
          {clips.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setActive(c)}
              className={`w-full rounded-xl border p-3 text-left transition ${
                active?.id === c.id
                  ? "border-emerald-500/40 bg-emerald-500/5"
                  : "border-zinc-800 bg-zinc-900/30 hover:border-zinc-700"
              }`}
            >
              <div className="flex flex-wrap items-center gap-2 text-[11px] text-zinc-500">
                <span className="text-emerald-400/90">{c.author}</span>
                <span className="rounded bg-emerald-500/10 px-1.5 py-0.5 text-emerald-300">
                  {c.score}
                </span>
                <span className="tabular-nums text-sky-300/90">
                  {c.start_s}–{c.end_s}s · {c.duration_s}s
                </span>
              </div>
              <div className="mt-1 text-sm font-medium text-zinc-100">
                {c.title_zh}
              </div>
              <p className="mt-1 line-clamp-2 text-xs text-zinc-500">
                {c.reason || c.summary}
              </p>
              {(c.topics || []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {c.topics.slice(0, 5).map((t) => (
                    <span
                      key={t}
                      className="rounded bg-zinc-800 px-1.5 py-0.5 text-[10px] text-zinc-400"
                    >
                      {TOPIC_LABEL[t] || t}
                    </span>
                  ))}
                </div>
              )}
            </button>
          ))}
        </div>

        <aside className="space-y-3 lg:sticky lg:top-6 lg:self-start">
          <div className="overflow-hidden rounded-xl border border-zinc-800 bg-black">
            {active?.has_video && active.video_url ? (
              <video
                key={active.id}
                className="aspect-video w-full"
                controls
                playsInline
                preload="metadata"
                src={`${active.video_url}#t=${active.start_s},${active.end_s}`}
              />
            ) : (
              <div className="flex aspect-video items-center justify-center text-sm text-zinc-600">
                选择左侧切片预览
              </div>
            )}
          </div>
          {active && (
            <div className="space-y-2 rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
              <div className="text-xs text-zinc-500">
                {active.author} · {active.start_s}–{active.end_s}s
              </div>
              <h2 className="text-sm font-medium text-zinc-100">
                {active.title_zh}
              </h2>
              <p className="text-xs leading-relaxed text-zinc-400">
                {active.reason}
              </p>
              <p className="text-xs leading-relaxed text-zinc-500">
                {active.summary}
              </p>
              <div className="flex flex-wrap gap-2 pt-1 text-xs">
                <Link
                  href={active.detail_url || `/library/${active.aweme_id}`}
                  className="rounded-lg bg-emerald-600/90 px-3 py-1.5 text-white hover:bg-emerald-500"
                >
                  打开课条详情
                </Link>
                <a
                  href={`/api/media/${active.path}/extract.json`}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-lg border border-zinc-700 px-3 py-1.5 text-zinc-300 hover:border-zinc-500"
                >
                  extract.json
                </a>
              </div>
              <pre className="overflow-auto rounded-lg bg-zinc-950 p-2 text-[10px] text-zinc-500">
                {JSON.stringify(
                  {
                    id: active.id,
                    aweme_id: active.aweme_id,
                    path: active.path,
                    start_s: active.start_s,
                    end_s: active.end_s,
                    reason: active.reason,
                    topics: active.topics,
                  },
                  null,
                  2,
                )}
              </pre>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
